import React from "react";
import { Box, Link, Typography } from "@mui/material";

export type GWorkspaceConnectionCheck = {
  id: string;
  label: string;
  status: string;
  detail?: string;
  enable_url?: string;
};

const statusColor = (status: string) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "success") return "#16a34a";
  // Amber: the tenant is fine, the account is short of something (a licence). Grey: not checked.
  if (normalized === "warning") return "#d97706";
  if (normalized === "unknown") return "#6b7280";
  return "#dc2626";
};

const GWorkspaceConnectionChecksResultsBox: React.FC<{
  checks: GWorkspaceConnectionCheck[];
  title?: string;
}> = ({ checks, title = "API enablement checks" }) => {
  if (!checks?.length) return null;

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        border: "1px solid #e5e5e5",
        borderRadius: 1,
        bgcolor: "#fff",
      }}
    >
      <Typography
        sx={{
          fontFamily: "var(--theo-font-sans)",
          fontWeight: 600,
          fontSize: 13,
          color: "#1a1a1a",
          mb: 1,
        }}
      >
        {title}
      </Typography>
      {checks.map((check) => (
        <Box
          key={check.id}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
            py: 0.5,
            borderBottom: "1px solid #f0f0f0",
            "&:last-child": { borderBottom: "none" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: statusColor(check.status),
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#333", flex: 1 }}>
              {check.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: "var(--theo-font-sans)",
                fontSize: 12,
                fontWeight: 600,
                color: statusColor(check.status),
                textTransform: "capitalize",
              }}
            >
              {check.status}
            </Typography>
          </Box>
          {check.detail ? (
            <Typography
              sx={{
                fontFamily: "var(--theo-font-sans)",
                fontSize: 12,
                color: "#6b7280",
                pl: 2.25,
                lineHeight: 1.4,
              }}
            >
              {check.detail}
            </Typography>
          ) : null}
          {check.status !== "success" && check.enable_url ? (
            <Box sx={{ pl: 2.25, mt: 0.5 }}>
              <Typography
                component="div"
                sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, color: "#4b5563", lineHeight: 1.5 }}
              >
                1. Open the{" "}
                <Link
                  href={check.enable_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: "#705d4a", fontWeight: 600 }}
                >
                  {check.label} page
                </Link>
                .
              </Typography>
              <Typography
                sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, color: "#4b5563", lineHeight: 1.5 }}
              >
                2. Select the Google Cloud project that owns this service account, then click Enable.
              </Typography>
              <Typography
                sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, color: "#4b5563", lineHeight: 1.5 }}
              >
                3. Wait a few minutes for propagation, then click Test again.
              </Typography>
            </Box>
          ) : null}
        </Box>
      ))}
    </Box>
  );
};

export default GWorkspaceConnectionChecksResultsBox;
